import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import axios from 'axios';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './WorkshopCalendar.css';

const localizer = momentLocalizer(moment);

function WorkshopCalendar() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [workshopTypes, setWorkshopTypes] = useState([]);
  const [viewMode, setViewMode] = useState('feed');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get('/api/workshops/locations');
        const locs = response.data || [];
        setLocations(locs);
        if (locs.length > 0) {
          setSelectedLocationId(locs[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch locations:', err);
      }
    };

    const fetchTypes = async () => {
      try {
        const response = await axios.get('/api/workshops/types');
        setWorkshopTypes(response.data || []);
      } catch (err) {
        console.error('Failed to fetch workshop types:', err);
      }
    };

    fetchLocations();
    fetchTypes();
  }, []);

  const fetchSessions = useCallback(async (date, locationId) => {
    if (!locationId) return;
    try {
      setLoading(true);
      const startDate = moment(date).startOf('month').subtract(7, 'days').format('YYYY-MM-DD');
      const endDate = moment(date).endOf('month').add(7, 'days').format('YYYY-MM-DD');

      const response = await axios.get('/api/workshops/sessions', {
        params: { startDate, endDate, locationId }
      });

      setSessions(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch sessions for calendar:', err);
      setError('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedLocationId) {
      fetchSessions(currentDate, selectedLocationId);
    }
  }, [currentDate, selectedLocationId, fetchSessions]);

  // Transform sessions into calendar events
  const events = useMemo(() => {
    return sessions.map((session) => {
      const typeName = session.workshop_types?.name || 'Workshop';
      const typeColor = session.workshop_types?.color || '#8B6F4E';
      const typeId = session.workshop_types?.id || null;

      return {
        id: session.id,
        title: `${typeName} (${session.booked_count || 0}/${session.capacity})`,
        start: new Date(`${session.session_date}T${session.start_time}`),
        end: new Date(`${session.session_date}T${session.end_time}`),
        resource: {
          typeColor,
          typeId,
          typeName,
          status: session.status,
          capacity: session.capacity,
          bookedCount: session.booked_count || 0
        }
      };
    });
  }, [sessions]);

  // Build feed cards: group sessions by date then by workshop type
  const feedCards = useMemo(() => {
    const grouped = {};

    for (const session of sessions) {
      const date = session.session_date;
      const typeId = session.workshop_types?.id || 'unknown';
      const key = `${date}__${typeId}`;

      if (!grouped[key]) {
        grouped[key] = {
          date,
          typeId,
          typeName: session.workshop_types?.name || 'Workshop',
          typeColor: session.workshop_types?.color || '#8B6F4E',
          slots: []
        };
      }

      grouped[key].slots.push({
        id: session.id,
        startTime: session.start_time,
        endTime: session.end_time,
        capacity: session.capacity,
        bookedCount: session.booked_count || 0,
        status: session.status
      });
    }

    // Sort cards by date, then by type name; sort slots by start time
    const cards = Object.values(grouped);
    cards.sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return a.typeName.localeCompare(b.typeName);
    });
    for (const card of cards) {
      card.slots.sort((a, b) => a.startTime.localeCompare(b.startTime));
    }

    return cards;
  }, [sessions]);

  const handleNavigate = (date) => {
    setCurrentDate(date);
  };

  const handleSelectEvent = (event) => {
    navigate(`/workshops/sessions/${event.id}`);
  };

  const eventStyleGetter = (event) => {
    const bgColor = event.resource?.typeColor || '#8B6F4E';
    const isCancelled = event.resource?.status === 'cancelled';

    return {
      style: {
        backgroundColor: isCancelled ? '#999' : bgColor,
        borderRadius: '4px',
        opacity: isCancelled ? 0.5 : 1,
        color: 'white',
        border: 'none',
        fontSize: '12px',
        padding: '2px 6px',
        textDecoration: isCancelled ? 'line-through' : 'none'
      }
    };
  };

  const formatTime = (timeStr) => {
    const [h, m] = timeStr.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const display = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${display}:${m} ${ampm}`;
  };

  const getAvailColor = (booked, capacity) => {
    const ratio = booked / capacity;
    if (ratio >= 1) return '#c62828';
    if (ratio >= 0.5) return '#e65100';
    return '#2e7d32';
  };

  const selectedLocation = locations.find((l) => l.id === selectedLocationId);

  // Group feed cards by date for rendering with date headers
  const feedByDate = useMemo(() => {
    const dateGroups = [];
    let currentDateGroup = null;

    for (const card of feedCards) {
      if (!currentDateGroup || currentDateGroup.date !== card.date) {
        currentDateGroup = { date: card.date, cards: [] };
        dateGroups.push(currentDateGroup);
      }
      currentDateGroup.cards.push(card);
    }

    return dateGroups;
  }, [feedCards]);

  return (
    <div className="workshop-calendar">
      <div className="ws-page-header">
        <h1>Workshop Calendar</h1>
        <button className="ws-btn-primary" onClick={() => navigate('/workshops/sessions')}>
          List View
        </button>
      </div>

      {error && <div className="ws-error">{error}</div>}

      {locations.length > 0 && (
        <div className="ws-location-tabs">
          {locations.map((loc) => (
            <button
              key={loc.id}
              className={`ws-location-tab ${selectedLocationId === loc.id ? 'ws-location-tab-active' : ''}`}
              onClick={() => setSelectedLocationId(loc.id)}
            >
              {loc.name}
            </button>
          ))}
        </div>
      )}

      {selectedLocation && (
        <p style={{ color: '#666', fontSize: '13px', margin: '0 0 12px' }}>
          {selectedLocation.city}, {selectedLocation.state}
        </p>
      )}

      <div className="ws-view-toggle">
        <button
          className={`ws-view-btn ${viewMode === 'feed' ? 'ws-view-btn-active' : ''}`}
          onClick={() => setViewMode('feed')}
        >
          Feed
        </button>
        <button
          className={`ws-view-btn ${viewMode === 'calendar' ? 'ws-view-btn-active' : ''}`}
          onClick={() => setViewMode('calendar')}
        >
          Calendar
        </button>
      </div>

      <div className="ws-calendar-legend">
        {workshopTypes.map((type) => (
          <div key={type.id} className="ws-legend-item">
            <span className="ws-legend-dot" style={{ backgroundColor: type.color || '#8B6F4E' }}></span>
            <span className="ws-legend-label">{type.name}</span>
          </div>
        ))}
      </div>

      {viewMode === 'calendar' && (
        <div className="ws-calendar-wrapper">
          {loading && (
            <div className="ws-calendar-loading">Loading...</div>
          )}
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 700 }}
            onNavigate={handleNavigate}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventStyleGetter}
            views={['month', 'week', 'day']}
            defaultView="month"
            popup
          />
        </div>
      )}

      {viewMode === 'feed' && (
        <div className="ws-feed-container">
          {loading && <div className="ws-feed-loading">Loading...</div>}
          {!loading && feedByDate.length === 0 && (
            <div className="ws-feed-empty">No sessions scheduled for this period</div>
          )}
          {feedByDate.map((dateGroup) => (
            <div key={dateGroup.date} className="ws-feed-date-group">
              <div className="ws-feed-date-header">
                {moment(dateGroup.date).format('ddd, MMM D')}
              </div>
              {dateGroup.cards.map((card) => (
                <div
                  key={`${card.date}-${card.typeId}`}
                  className="ws-feed-card"
                  style={{ borderLeftColor: card.typeColor }}
                >
                  <div className="ws-feed-card-title">
                    <span className="ws-feed-type-dot" style={{ backgroundColor: card.typeColor }} />
                    {card.typeName}
                  </div>
                  <div className="ws-feed-slots">
                    {card.slots.map((slot) => {
                      const available = slot.capacity - slot.bookedCount;
                      const isFull = available <= 0;
                      const isCancelled = slot.status === 'cancelled';
                      const fillPct = slot.capacity > 0 ? Math.min((slot.bookedCount / slot.capacity) * 100, 100) : 0;

                      return (
                        <div
                          key={slot.id}
                          className={`ws-feed-slot ${isCancelled ? 'ws-feed-slot-cancelled' : ''}`}
                          onClick={() => navigate(`/workshops/sessions/${slot.id}`)}
                        >
                          <span className="ws-feed-slot-time">
                            {formatTime(slot.startTime)}
                          </span>
                          <div className="ws-feed-capacity-bar">
                            <div
                              className="ws-feed-capacity-fill"
                              style={{
                                width: `${fillPct}%`,
                                backgroundColor: isCancelled ? '#999' : getAvailColor(slot.bookedCount, slot.capacity)
                              }}
                            />
                          </div>
                          <span
                            className="ws-feed-avail-text"
                            style={{
                              color: isCancelled ? '#999' : isFull ? '#c62828' : '#525250'
                            }}
                          >
                            {isCancelled
                              ? 'Cancelled'
                              : isFull
                                ? 'FULL'
                                : `${available} of ${slot.capacity} avail`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default WorkshopCalendar;
